/**
 * Place Type Detection Hook
 * Detects the type of environment based on GPS coordinates
 * Used for location-based music recommendations
 */

import { useState, useCallback } from 'react';
import { Geolocation } from '@capacitor/geolocation';
import { logger } from '@/lib/logger';

export type PlaceType = 
  | 'beach'      // Praia, orla, litoral
  | 'park'       // Parque, praça, área verde
  | 'urban'      // Centro urbano, ruas movimentadas
  | 'trail'      // Trilha, montanha, natureza
  | 'gym'        // Academia, indoor
  | 'residential' // Bairro residencial
  | 'stadium'    // Pista de atletismo, estádio
  | 'unknown';

export interface LocationContext {
  city?: string;
  neighborhood?: string;
  state?: string;
  country?: string;
  placeType: PlaceType;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  detectedAt?: string;
}

interface UsePlaceTypeReturn {
  locationContext: LocationContext | null;
  isDetecting: boolean;
  detectPlaceType: () => Promise<LocationContext>;
  setManualPlaceType: (placeType: PlaceType) => void;
  placeTypeLabels: Record<PlaceType, string>;
  placeTypeIcons: Record<PlaceType, string>;
}

// Labels in Portuguese
const PLACE_TYPE_LABELS: Record<PlaceType, string> = {
  beach: 'Praia',
  park: 'Parque',
  urban: 'Centro Urbano',
  trail: 'Trilha',
  gym: 'Academia',
  residential: 'Bairro',
  stadium: 'Pista',
  unknown: 'Desconhecido',
};

const PLACE_TYPE_ICONS: Record<PlaceType, string> = {
  beach: '🏖️',
  park: '🌳',
  urban: '🏙️',
  trail: '🏔️',
  gym: '🏋️',
  residential: '🏘️',
  stadium: '🏟️',
  unknown: '📍',
};

// Simple heuristics for place type detection based on coordinate patterns
// In production, this would use Google Places API or similar
async function detectPlaceTypeFromCoordinates(
  latitude: number,
  longitude: number
): Promise<PlaceType> {
  try {
    // Use reverse geocoding to get address components
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=18`
    );
    
    if (!response.ok) {
      logger.warn('Reverse geocoding failed', { status: response.status });
      return 'unknown';
    }

    const data = await response.json();
    const addressType = data.type?.toLowerCase() || '';
    const addressClass = data.class?.toLowerCase() || '';
    const displayName = data.display_name?.toLowerCase() || '';

    // Check for beach indicators
    if (
      displayName.includes('praia') ||
      displayName.includes('beach') ||
      displayName.includes('orla') ||
      displayName.includes('litoral') ||
      addressType.includes('beach')
    ) {
      return 'beach';
    }

    // Check for park/nature indicators
    if (
      addressClass === 'leisure' ||
      addressType.includes('park') ||
      displayName.includes('parque') ||
      displayName.includes('praça') ||
      displayName.includes('jardim') ||
      addressType.includes('nature_reserve')
    ) {
      return 'park';
    }

    // Check for trail/mountain indicators
    if (
      addressType.includes('path') ||
      addressType.includes('track') ||
      displayName.includes('trilha') ||
      displayName.includes('montanha') ||
      displayName.includes('serra') ||
      addressClass === 'natural'
    ) {
      return 'trail';
    }

    // Check for stadium/sports indicators
    if (
      addressType.includes('stadium') ||
      addressType.includes('sports') ||
      displayName.includes('estádio') ||
      displayName.includes('pista') ||
      displayName.includes('atletismo')
    ) {
      return 'stadium';
    }

    // Check for urban center indicators
    if (
      addressClass === 'highway' ||
      addressType.includes('commercial') ||
      addressType.includes('retail') ||
      displayName.includes('centro') ||
      displayName.includes('avenida')
    ) {
      return 'urban';
    }

    // Default to residential for most addresses
    if (
      addressType.includes('residential') ||
      addressType.includes('house') ||
      addressClass === 'building'
    ) {
      return 'residential';
    }

    return 'unknown';
  } catch (error) {
    logger.error('Place type detection failed', error);
    return 'unknown';
  }
}

async function getAddressFromCoordinates(
  latitude: number,
  longitude: number
): Promise<{ city?: string; neighborhood?: string; state?: string; country?: string }> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`
    );

    if (!response.ok) return {};

    const data = await response.json();
    const address = data.address || {};

    return {
      city: address.city || address.town || address.municipality,
      neighborhood: address.suburb || address.neighbourhood || address.district,
      state: address.state,
      country: address.country,
    };
  } catch (error) {
    logger.error('Address lookup failed', error);
    return {};
  }
}

export function usePlaceType(): UsePlaceTypeReturn {
  const [locationContext, setLocationContext] = useState<LocationContext | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);

  const detectPlaceType = useCallback(async (): Promise<LocationContext> => {
    setIsDetecting(true);
    
    try {
      // Get current position
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });

      const { latitude, longitude } = position.coords;

      // Detect place type and get address in parallel
      const [placeType, address] = await Promise.all([
        detectPlaceTypeFromCoordinates(latitude, longitude),
        getAddressFromCoordinates(latitude, longitude),
      ]);

      const context: LocationContext = {
        placeType,
        coordinates: { latitude, longitude },
        city: address.city,
        neighborhood: address.neighborhood,
        state: address.state,
        country: address.country,
        detectedAt: new Date().toISOString(),
      };

      setLocationContext(context);
      
      logger.info('Place type detected', {
        placeType,
        city: address.city,
        neighborhood: address.neighborhood,
      });

      return context;
    } catch (error) {
      logger.error('Failed to detect place type', error);
      
      const fallbackContext: LocationContext = {
        placeType: 'unknown',
        detectedAt: new Date().toISOString(),
      };
      
      setLocationContext(fallbackContext);
      return fallbackContext;
    } finally {
      setIsDetecting(false);
    }
  }, []);

  const setManualPlaceType = useCallback((placeType: PlaceType) => {
    setLocationContext(prev => ({
      ...prev,
      placeType,
      detectedAt: new Date().toISOString(),
    }));
  }, []);

  return {
    locationContext,
    isDetecting,
    detectPlaceType,
    setManualPlaceType,
    placeTypeLabels: PLACE_TYPE_LABELS,
    placeTypeIcons: PLACE_TYPE_ICONS,
  };
}
